import React, { useState, useEffect } from 'react';
import { trackEvent } from '../analytics/GoogleAnalytics';
import { Helmet } from 'react-helmet';
import AuthorCredit from '../ui/AuthorCredit';
import FreshnessDate, { getFreshnessDateIso } from '../ui/FreshnessDate';
import { DIGITAL_LINKS, POSITIONING } from '../../compliance/mtdFacts';

const DigitalLinkChecker = () => {
  const [q1, setQ1] = useState('');
  const [q2, setQ2] = useState('');
  const [q3, setQ3] = useState('');

  const needsCompatibleSoftware = q1 === 'manual';
  const copyPasteRisk = q2 === 'copy-paste';
  const paperOnly = q3 === 'paper';
  const hasGap = needsCompatibleSoftware || copyPasteRisk || paperOnly;

  useEffect(() => {
    if (q1 && q2 && q3) {
      trackEvent('tool_completion', { tool_name: 'digital_link_diagnostic' });
      if (hasGap) {
        trackEvent('compliance_fail', { reason: 'manual_or_paper_records' });
      }
    }
  }, [q1, q2, q3, hasGap]);

  const schema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to keep digital records for MTD for Income Tax",
    "dateModified": getFreshnessDateIso(),
    "step": [
      { "@type": "HowToStep", "text": "Create digital records of income and expenses in compatible software. Typing original entries into that software is allowed." },
      { "@type": "HowToStep", "text": DIGITAL_LINKS.summary },
      { "@type": "HowToStep", "text": `Send quarterly updates and the tax return through compatible software. ${POSITIONING.noSubmit}` }
    ]
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-slate-900/50 backdrop-blur-xl border border-white/5 rounded-3xl p-8 shadow-2xl my-12">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <FreshnessDate className="mb-4" />
      <div className="mb-8">
        <h3 className="text-3xl font-extrabold text-white mb-4">The "Digital Link" Diagnostic</h3>

        <AuthorCredit />

        <div className="bg-indigo-500/10 border border-indigo-500/30 p-6 rounded-2xl mb-6 shadow-lg">
          <p className="text-xl font-medium leading-relaxed">
            <strong>{DIGITAL_LINKS.summary}</strong>
          </p>
        </div>

        <div className="prose prose-invert max-w-none text-slate-300 mb-8">
          <p>
            Under <strong>Making Tax Digital for Income Tax</strong>, records must be kept digitally in compatible software. {DIGITAL_LINKS.photoNote} {POSITIONING.noSubmit}
          </p>
        </div>
        <p className="text-slate-400 text-sm font-semibold">Answer 3 questions below to see how your current workflow maps to HMRC's digital-record rules.</p>
      </div>

      <div className="space-y-8">
        <div className="space-y-4">
          <label className="block text-sm font-semibold text-slate-300">1. How do you send quarterly updates to HMRC?</label>
          <div className="grid grid-cols-1 gap-3">
            {[
              { id: 'api', label: 'Compatible software / API', sub: 'Software that works with MTD for Income Tax' },
              { id: 'bridging', label: 'Bridging software', sub: 'Records in a spreadsheet, submitted via a bridge' },
              { id: 'manual', label: 'Typing into the HMRC website', sub: 'Old Self Assessment pages, not compatible software' }
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setQ1(opt.id)}
                className={`flex flex-col items-start p-4 rounded-xl border transition-all ${q1 === opt.id ? 'bg-indigo-500/20 border-indigo-500 shadow-lg shadow-indigo-500/10' : 'bg-black/20 border-white/5 hover:border-white/10'
                  }`}
              >
                <span className="text-white font-medium">{opt.label}</span>
                <span className="text-slate-500 text-xs mt-1">{opt.sub}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <label className="block text-sm font-semibold text-slate-300">2. After a digital record exists, do you copy-paste it between products?</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { id: 'link', label: 'No — they are digitally linked', sub: 'CSV import/export, spreadsheet emailed for import, or API' },
              { id: 'copy-paste', label: 'Yes, I copy-paste existing records', sub: 'Not allowed after that record has been sent in a quarterly update' }
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setQ2(opt.id)}
                className={`flex flex-col items-start p-4 rounded-xl border transition-all ${q2 === opt.id ? 'bg-indigo-500/20 border-indigo-500 shadow-lg shadow-indigo-500/10' : 'bg-black/20 border-white/5 hover:border-white/10'
                  }`}
              >
                <span className="text-white font-medium">{opt.label}</span>
                <span className="text-slate-500 text-xs mt-1">{opt.sub}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <label className="block text-sm font-semibold text-slate-300">3. How do you capture receipts today?</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { id: 'digital', label: 'Photo, email, or app', sub: 'Still needs a digital record in compatible software' },
              { id: 'paper', label: 'Paper only', sub: 'Supporting documents still need a digital record' }
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setQ3(opt.id)}
                className={`flex flex-col items-start p-4 rounded-xl border transition-all ${q3 === opt.id ? 'bg-indigo-500/20 border-indigo-500 shadow-lg shadow-indigo-500/10' : 'bg-black/20 border-white/5 hover:border-white/10'
                  }`}
              >
                <span className="text-white font-medium">{opt.label}</span>
                <span className="text-slate-500 text-xs mt-1">{opt.sub}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {q1 && q2 && q3 && (
        <div className={`mt-10 p-6 rounded-2xl border transition-all duration-500 shadow-2xl ${hasGap ? 'bg-amber-500/10 border-amber-500/30' : 'bg-emerald-500/10 border-emerald-500/30'
          }`}>
          <div className="flex flex-col items-center text-center">
            <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4 ${hasGap ? 'bg-amber-500 text-black shadow-lg shadow-amber-500/25' : 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
              }`}>
              {hasGap ? 'GAPS TO CHECK' : 'LOOKING GOOD'}
            </span>
            <h4 className="text-xl font-bold text-white mb-2">
              {hasGap ? 'This workflow still needs compatible software' : 'Your filing path looks ready for MTD'}
            </h4>
            <p className="text-slate-300 text-sm leading-relaxed max-w-md">
              {hasGap
                ? 'Quarterly updates must be sent through compatible software. Typing original entries into that software is allowed. Paper or photos are supporting records until someone creates a digital record and, if you use more than one product, a digital link such as CSV import. Zap does not replace that filing software.'
                : 'Keep using compatible software for quarterly updates. Make sure any CSV or other import from a capture tool is actually imported, not re-typed after the record has been sent to HMRC.'
              }
            </p>

            {hasGap && (
              <a
                href="/#signup"
                className="mt-6 px-10 py-4 bg-white text-slate-950 font-extrabold rounded-xl hover:bg-slate-200 transition-all uppercase tracking-wide text-sm shadow-xl"
              >
                Start capturing receipts with Zap
              </a>
            )}
          </div>
        </div>
      )}

      <div id="stat-digital-link-leakage" className="mt-10 mb-2 bg-black/40 border border-slate-800 p-6 rounded-xl shadow-inner relative overflow-hidden text-center md:text-left">
        <p className="text-emerald-400 font-bold uppercase tracking-widest text-xs mb-1">The "Reasonable Care" Loss</p>
        <p className="text-2xl font-extrabold text-white mb-2">31% of the HMRC Tax Gap</p>
        <p className="text-slate-400 text-sm">
          "HMRC attributes 31% of the UK's £46.8bn tax gap to 'Failure to Take Reasonable Care'—the largest single cause of lost revenue."
        </p>
        <p className="mt-3 text-[10px] text-slate-500 italic border-t border-slate-800/50 pt-2">
          Source: <a href="https://www.gov.uk/government/statistics/measuring-tax-gaps/7-tax-gaps-illustrative-tax-gap-by-behaviour" target="_blank" rel="noopener noreferrer" className="underline hover:text-emerald-400 transition-colors">HMRC, Tax gaps: illustrative tax gap by behaviour</a>
        </p>
      </div>

      <div className="mt-6 mb-2 text-center">
        <a href={DIGITAL_LINKS.source} target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-indigo-400 hover:text-indigo-300 underline">
          {DIGITAL_LINKS.sourceAlias} →
        </a>
      </div>


    </div>
  );
};

export default DigitalLinkChecker;
